import React, { useState, useEffect, useCallback } from 'react';
import { getAllVehicleChecks, approveVehicleCheck, rejectVehicleCheck } from '../services/vehicleCheckService';

const conditionLabel = (val) => {
  if (val === 'satisfactory') return { text: '✓', cls: 'text-green-600 font-bold' };
  if (val === 'defective')    return { text: '✗', cls: 'text-red-600 font-bold' };
  return { text: 'N/A', cls: 'text-gray-400' };
};

const ConditionRow = ({ label, condition, comment }) => {
  const { text, cls } = conditionLabel(condition);
  return (
    <div className={`flex items-start justify-between py-1.5 px-2 rounded text-sm ${condition === 'defective' ? 'bg-red-50' : ''}`}>
      <span className="text-gray-700 flex-1 pr-3">{label}</span>
      <div className="text-right">
        <span className={cls}>{text}</span>
        {comment && <p className="text-xs text-gray-500 italic mt-0.5">{comment}</p>}
      </div>
    </div>
  );
};

const VehicleCheckReviews = () => {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchChecks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAllVehicleChecks('pending');
      setChecks(data);
    } catch (error) {
      console.error('Error fetching checks:', error);
      setChecks([]);
      setError('Failed to load pending vehicle checks. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChecks(); }, [fetchChecks]);

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await approveVehicleCheck(selectedCheck.id, reviewNote);
      setChecks(prev => prev.filter(c => c.id !== selectedCheck.id));
      closeModal();
    } catch (error) {
      console.error('Error approving check:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!reviewNote.trim()) {
      alert('Please provide a reason for rejection.');
      return;
    }
    setProcessing(true);
    try {
      await rejectVehicleCheck(selectedCheck.id, reviewNote, selectedCheck);
      setChecks(prev => prev.filter(c => c.id !== selectedCheck.id));
      closeModal();
    } catch (error) {
      console.error('Error rejecting check:', error);
    } finally {
      setProcessing(false);
    }
  };

  const closeModal = () => { setSelectedCheck(null); setReviewNote(''); };

  const hasDefects = (check) => [
    check.externalBodyworkCondition, check.externalWiperBladesCondition,
    check.externalCleannessCondition, check.externalTyresCondition,
    check.interiorSeatBeltsCondition, check.interiorFirstAidKitCondition,
    check.interiorFireExtinguisherCondition, check.interiorTorchCondition,
    check.interiorWarningTriangleCondition, check.interiorLogBookCondition,
    check.interiorSpareTyreCondition, check.fluidsEngineOilCondition,
    check.fluidsCoolantCondition, check.fluidsWindscreenWashCondition,
    check.fluidsBrakeClutchCondition, check.fluidsPowerSteeringCondition,
    check.fluidsBatteryCondition, check.fluidsLeaksCondition,
    check.functionWarningLightCondition, check.functionAllLightsCondition,
    check.functionHornCondition, check.functionWashersWipersCondition,
    check.functionBrakeCondition, check.functionFuelCondition,
  ].includes('defective');

  const filtered = checks.filter(c => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return c.vehiclePlate?.toLowerCase().includes(s) ||
           c.driverName?.toLowerCase().includes(s) ||
           c.checkDate?.includes(s);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pending Reviews</h1>
          <p className="text-gray-500 text-sm mt-0.5">Vehicle checks awaiting your approval</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="bg-yellow-100 text-yellow-800 text-sm font-semibold px-3 py-1 rounded-full">
            {checks.length} pending
          </span>
          <input
            type="text"
            placeholder="Search driver or plate..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-56"
          />
        </div>
      </div>

      {/* List */}
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <p className="text-red-700 font-semibold">{error}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-gray-700 font-semibold text-lg">All caught up!</p>
          <p className="text-gray-400 text-sm mt-1">No pending vehicle checks to review.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Date</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Vehicle</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Driver</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Odometer</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Fuel</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Issues</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(check => (
                <tr key={check.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{check.checkDate || '—'}</td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900">{check.vehiclePlate}</p>
                    <p className="text-gray-500 text-xs">{check.vehicleInfo}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-gray-900">{check.driverName}</p>
                    <p className="text-gray-500 text-xs">{check.driverPhone}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{check.odometerReading ? check.odometerReading + ' km' : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-14 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-2 rounded-full ${check.fuelLevel >= 50 ? 'bg-green-500' : check.fuelLevel >= 25 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${check.fuelLevel || 0}%` }}
                        />
                      </div>
                      <span className="text-gray-700">{check.fuelLevel}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {hasDefects(check) ? (
                      <span className="flex items-center gap-1 text-red-600 font-semibold text-xs bg-red-50 px-2 py-1 rounded-full w-fit">
                        ✗ Has Issues
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-green-600 text-xs bg-green-50 px-2 py-1 rounded-full w-fit">
                        ✓ All Good
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => { setSelectedCheck(check); setReviewNote(''); }}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-semibold"
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail + Review Modal */}
      {selectedCheck && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={closeModal}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-4" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="bg-blue-600 text-white px-6 py-4 rounded-t-xl flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold">{selectedCheck.vehiclePlate} — {selectedCheck.vehicleInfo}</h2>
                <p className="text-blue-100 text-sm mt-0.5">Driver: {selectedCheck.driverName} | Date: {selectedCheck.checkDate}</p>
              </div>
              <button onClick={closeModal} className="text-white hover:text-blue-200 text-2xl font-bold leading-none ml-4">×</button>
            </div>

            <div className="p-6 space-y-5">
              {/* Vehicle info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-blue-50 rounded-lg p-4 text-sm">
                <div><p className="text-gray-500 text-xs">Odometer</p><p className="font-semibold">{selectedCheck.odometerReading ? selectedCheck.odometerReading + ' km' : '—'}</p></div>
                <div><p className="text-gray-500 text-xs">Fuel</p><p className="font-semibold">{selectedCheck.fuelLevel ? selectedCheck.fuelLevel + '%' : '—'}</p></div>
                <div><p className="text-gray-500 text-xs">Last Service</p><p className="font-semibold">{[selectedCheck.lastServiceMileage ? selectedCheck.lastServiceMileage + ' km' : null, selectedCheck.lastServiceDate].filter(Boolean).join(' / ') || '—'}</p></div>
                <div><p className="text-gray-500 text-xs">Next Service</p><p className="font-semibold">{selectedCheck.nextServiceMileage ? selectedCheck.nextServiceMileage + ' km' : '—'}</p></div>
                <div><p className="text-gray-500 text-xs">Control Technique</p><p className="font-semibold">{selectedCheck.controlTechniqueDate || '—'}</p></div>
                <div><p className="text-gray-500 text-xs">Insurance Expiry</p><p className="font-semibold">{selectedCheck.insuranceExpirationDate || '—'}</p></div>
                {selectedCheck.nextServiceMileage && selectedCheck.odometerReading && (
                  <div>
                    <p className="text-gray-500 text-xs">To Next Service</p>
                    <p className={`font-semibold ${Number(selectedCheck.nextServiceMileage) - Number(selectedCheck.odometerReading) <= 0 ? 'text-red-600' : Number(selectedCheck.nextServiceMileage) - Number(selectedCheck.odometerReading) <= 500 ? 'text-orange-500' : 'text-green-600'}`}>
                      {Number(selectedCheck.nextServiceMileage) - Number(selectedCheck.odometerReading) <= 0
                        ? `Overdue by ${Math.abs(Number(selectedCheck.nextServiceMileage) - Number(selectedCheck.odometerReading))} km`
                        : `${Number(selectedCheck.nextServiceMileage) - Number(selectedCheck.odometerReading)} km left`}
                    </p>
                  </div>
                )}
              </div>

              {/* Check sections grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">External Condition</h3>
                  <ConditionRow label="Bodywork, windscreen, windows, lights" condition={selectedCheck.externalBodyworkCondition} comment={selectedCheck.externalBodyworkComment} />
                  <ConditionRow label="Wiper blades" condition={selectedCheck.externalWiperBladesCondition} comment={selectedCheck.externalWiperBladesComment} />
                  <ConditionRow label="Cleanliness" condition={selectedCheck.externalCleannessCondition} comment={selectedCheck.externalCleannessComment} />
                  <ConditionRow label="Tyres & pressure" condition={selectedCheck.externalTyresCondition} comment={selectedCheck.externalTyresComment} />
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Interior & Equipment</h3>
                  <ConditionRow label="Seat belts" condition={selectedCheck.interiorSeatBeltsCondition} comment={selectedCheck.interiorSeatBeltsComment} />
                  <ConditionRow label="First aid kit" condition={selectedCheck.interiorFirstAidKitCondition} comment={selectedCheck.interiorFirstAidKitComment} />
                  <ConditionRow label="Fire extinguisher" condition={selectedCheck.interiorFireExtinguisherCondition} comment={selectedCheck.interiorFireExtinguisherComment} />
                  <ConditionRow label="Torch" condition={selectedCheck.interiorTorchCondition} comment={selectedCheck.interiorTorchComment} />
                  <ConditionRow label="Warning triangle" condition={selectedCheck.interiorWarningTriangleCondition} comment={selectedCheck.interiorWarningTriangleComment} />
                  <ConditionRow label="Log book" condition={selectedCheck.interiorLogBookCondition} comment={selectedCheck.interiorLogBookComment} />
                  <ConditionRow label="Spare tyre & jack" condition={selectedCheck.interiorSpareTyreCondition} comment={selectedCheck.interiorSpareTyreComment} />
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Fluids</h3>
                  <ConditionRow label="Engine oil" condition={selectedCheck.fluidsEngineOilCondition} comment={selectedCheck.fluidsEngineOilComment} />
                  <ConditionRow label="Coolant" condition={selectedCheck.fluidsCoolantCondition} comment={selectedCheck.fluidsCoolantComment} />
                  <ConditionRow label="Windscreen wash" condition={selectedCheck.fluidsWindscreenWashCondition} comment={selectedCheck.fluidsWindscreenWashComment} />
                  <ConditionRow label="Brake/clutch fluid" condition={selectedCheck.fluidsBrakeClutchCondition} comment={selectedCheck.fluidsBrakeClutchComment} />
                  <ConditionRow label="Power steering fluid" condition={selectedCheck.fluidsPowerSteeringCondition} comment={selectedCheck.fluidsPowerSteeringComment} />
                  <ConditionRow label="Battery" condition={selectedCheck.fluidsBatteryCondition} comment={selectedCheck.fluidsBatteryComment} />
                  <ConditionRow label="Oil/waste leaks" condition={selectedCheck.fluidsLeaksCondition} comment={selectedCheck.fluidsLeaksComment} />
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Function Checks</h3>
                  <ConditionRow label="Warning light" condition={selectedCheck.functionWarningLightCondition} comment={selectedCheck.functionWarningLightComment} />
                  <ConditionRow label="All lights" condition={selectedCheck.functionAllLightsCondition} comment={selectedCheck.functionAllLightsComment} />
                  <ConditionRow label="Horn" condition={selectedCheck.functionHornCondition} comment={selectedCheck.functionHornComment} />
                  <ConditionRow label="Washers & wipers" condition={selectedCheck.functionWashersWipersCondition} comment={selectedCheck.functionWashersWipersComment} />
                  <ConditionRow label="Brake" condition={selectedCheck.functionBrakeCondition} comment={selectedCheck.functionBrakeComment} />
                  <ConditionRow label="Fuel" condition={selectedCheck.functionFuelCondition} comment={selectedCheck.functionFuelComment} />
                </div>
              </div>

              {selectedCheck.vehicleNotes && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                  <span className="font-semibold text-yellow-800">Notes: </span>
                  <span className="text-yellow-700">{selectedCheck.vehicleNotes}</span>
                </div>
              )}

              {/* Review actions */}
              <div className="border-t pt-4 space-y-3">
                <label className="block text-sm font-semibold text-gray-700">
                  Review Note <span className="text-red-500 font-normal">(required for rejection)</span>
                </label>
                <textarea
                  value={reviewNote}
                  onChange={e => setReviewNote(e.target.value)}
                  placeholder="Add a note for the driver..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <div className="flex justify-end gap-3">
                  <button onClick={closeModal} className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50">
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={processing}
                    className="px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
                  >
                    {processing ? 'Processing...' : 'Reject'}
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={processing}
                    className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                  >
                    {processing ? 'Processing...' : 'Approve'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleCheckReviews;
